import React, { useEffect, useRef, useState } from 'react';
import { EnvelopeSimple, Spinner, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

// Lightweight email validation — Pydantic's EmailStr enforces the real rules
// server-side; this is just a UX hint to avoid a round-trip for obvious typos.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Modal for the "Send to client" Export menu item. Captures the recipient
// email + optional note, kicks off a /wayback/share build, then immediately
// POSTs to /wayback/share/{token}/email so the homeowner gets a branded
// SendGrid email with the time-lapse link.
//
// Props
//   config   { payloadBuilder, projectName, projectAddress } | null
//             — null hides the dialog. payloadBuilder() returns the body to
//               POST to /wayback/share (so we capture map state at click time
//               rather than at render time).
//   apiCall  axios-like helper from useAuth
//   onClose  () => void — called on any dismiss
export function SendToClientModal({ config, apiCall, onClose }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'sent'
  const [error, setError] = useState(null);
  const emailInputRef = useRef(null);

  // Reset every time the modal reopens so a previous Send result doesn't
  // bleed into the new session. Also autofocus the email field so the user
  // can start typing immediately.
  useEffect(() => {
    if (!config) return;
    setEmail('');
    setName('');
    setNote('');
    setStatus('idle');
    setError(null);
    // RAF so the input is mounted before we focus.
    const id = requestAnimationFrame(() => emailInputRef.current?.focus?.());
    return () => cancelAnimationFrame(id);
  }, [config]);

  if (!config) return null;
  const { payloadBuilder, projectName, projectAddress } = config;
  const emailValid = EMAIL_REGEX.test(email.trim());
  const canSubmit = emailValid && status !== 'sending';

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setStatus('sending');
    setError(null);
    try {
      // Step 1 — create the share placeholder. POST returns immediately with
      // {token, url, status:'building'}.
      const shareBody = payloadBuilder();
      const shareRes = await apiCall('post', '/wayback/share', shareBody);
      const token = shareRes?.data?.token;
      if (!token) throw new Error('Could not create share link.');
      // Step 2 — fire the email with the recipient + note. We don't wait for
      // the background MP4 build to complete; the recipient's link resolves
      // to the friendly "preparing video" page until the encode lands.
      const emailRes = await apiCall('post', `/wayback/share/${token}/email`, {
        recipient_email: email.trim(),
        recipient_name: name.trim() || null,
        message: note.trim() || null,
        project_name: projectName || null,
        property_address: projectAddress || null,
      });
      setStatus('sent');
      const replyTo = emailRes?.data?.reply_to;
      toast.success(`Email sent to ${email.trim()}`, {
        description: replyTo
          ? `Replies will go to ${replyTo}. The link works for 7 days.`
          : 'The link works for 7 days — recipients see a preparing-video page until the MP4 finishes rendering.',
      });
      // Auto-close after a beat so the success state registers visually.
      setTimeout(() => onClose?.(), 1400);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not send email.';
      console.warn('Send-to-client failed:', detail);
      setError(typeof detail === 'string' ? detail : 'Could not send email.');
      setStatus('idle');
    }
  };

  return (
    <Dialog open={!!config} onOpenChange={(o) => { if (!o && status !== 'sending') onClose?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="send-to-client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EnvelopeSimple size={18} weight="duotone" className="text-primary" />
            Send time-lapse to client
          </DialogTitle>
          <DialogDescription className="text-xs">
            {projectName
              ? `We'll email a branded RoofMate link to your client's inbox for "${projectName}". The MP4 renders in the background.`
              : "We'll email a branded RoofMate link to your client's inbox. The MP4 renders in the background."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 mb-1 block">
              Recipient email <span className="text-rose-500">*</span>
            </label>
            <input
              ref={emailInputRef}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="client@example.com"
              className="w-full px-3 py-2 rounded-md text-sm bg-secondary/40 border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              data-testid="send-to-client-email-input"
              disabled={status !== 'idle'}
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 mb-1 block">
              Recipient name <span className="opacity-50 normal-case font-normal">(optional — used in the greeting)</span>
            </label>
            <input
              type="text"
              maxLength={80}
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sarah"
              className="w-full px-3 py-2 rounded-md text-sm bg-secondary/40 border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
              data-testid="send-to-client-name-input"
              disabled={status !== 'idle'}
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold opacity-60 mb-1 block">
              Personal note <span className="opacity-50 normal-case font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              maxLength={600}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Hi Sarah — here's the 5-year time-lapse of your roof I mentioned. Let me know what you think!"
              className="w-full px-3 py-2 rounded-md text-sm bg-secondary/40 border border-border focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors resize-none leading-relaxed"
              data-testid="send-to-client-note-input"
              disabled={status !== 'idle'}
            />
            <div className="text-[10px] opacity-50 mt-0.5 text-right">{note.length}/600</div>
          </div>

          {error && (
            <div className="text-[12px] text-rose-500 bg-rose-500/10 border border-rose-500/20 rounded-md px-2.5 py-1.5" data-testid="send-to-client-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${status === 'sent' ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:opacity-90'}`}
            data-testid="send-to-client-submit-btn"
          >
            {status === 'sending'
              ? <><Spinner size={14} className="animate-spin" /> Sending…</>
              : status === 'sent'
                ? <><CheckCircle size={14} weight="bold" /> Sent!</>
                : <><PaperPlaneTilt size={14} weight="bold" /> Send time-lapse</>}
          </button>

          <p className="text-[10px] text-center opacity-60 leading-relaxed">
            Recipients open the link to watch the MP4 inline — no app needed.
            Replies land in your inbox, not RoofMate's. Link is valid for 7 days.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
